import SafeSessionStorage from './storage/SafeSessionStorage';

const INTERACTION_SESSION_ID_STORAGE_KEY = 'interactionSession.id';
const INTERACTION_SESSION_ID_PREV_STORAGE_KEY = 'interactionSession.id.prev';
const INTERACTION_SESSION_EXPIRY_STORAGE_KEY = 'interactionSession.expiry';

export const DEFAULT_SESSION_EXPIRY_TIME_MS: number = 30 * 60 * 1000; // 30 minutes
export const DEFAULT_PERSISTED_SESSION_TTL_MS: number = 24 * 60 * 60 * 1000; // 24 hours
export const DEFAULT_EXPIRY_DEBOUNCE: number = 5 * 1000; // 5 seconds
export const DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE = 0.9; // 90% of expiry

const INTEGER_REGEX = /^\d+$/;

// WARNING: This value is shared between @atlaskit/analytics-cross-product and
// @atlassiansox/analytics-cross-product-interaction-client. Take care when updating.
export const INTERACTION_SESSION_ID_UPDATED_EVENT = 'interactionSessionIdUpdated';

/**
 * This class is essentially a wrapper around SessionStorage that introduces some failsafes (such as switching to InMemoryStorage if SessionStorage is not available)
 * and providing resilient methods for accessing and updating the interaction session ID.
 * This class can be initialised anywhere within a document, and `getCurrentInteractionSessionId` will always return the latest interaction session ID.
 *
 * The `setAnalyticsCallback` method must be called with a valid analytics event callback function provided by an Analytics Web Client before any other methods are used.
 * This is to ensure that any generated or renewed session IDs can be reported via an analytics event.
 *
 * The `setInteractionSessionId` method will write the SessionStorage with the persisted session ID, and is only intended to be used by the Analytics Web Client
 */
export default class InteractionSessionTracking {
	private safeSessionStorage: SafeSessionStorage;
	private sessionExpiryTime: number;
	private sessionExpiryDebounce: number;
	private sessionExpiryDebounceThresholdPercentage: number;
	private sessionExpiryDebounceTimer?: number;
	private analyticsReady: boolean = false;

	// This callback function will be called whenever a new session ID is generated
	// It expects a function that sends an operational analytics event, with
	// the first parameter being set to the action ('started' or 'renewed'), and the second parameter being
	// an InteractionSession object containing the new session ID and previous session ID.
	private sendEventCallback?: Function;

	constructor() {
		this.sessionExpiryTime = DEFAULT_SESSION_EXPIRY_TIME_MS;
		this.sessionExpiryDebounce = DEFAULT_EXPIRY_DEBOUNCE;
		this.sessionExpiryDebounceThresholdPercentage = DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE;

		this.safeSessionStorage = new SafeSessionStorage();
	}

	getCurrentInteractionSessionId(): string | null {
		try {
			const { isValidSession, sessionExpiry, sessionId } = this.getCurrentSessionParams();

			// If we have an existing valid session we can return the sessionId straight away
			if (isValidSession) {
				this.debouncedUpdateSessionExpiry(sessionExpiry);
				return sessionId;
			}

			// To generate a new session ID we must ensure analytics is ready
			if (this.analyticsReady) {
				// Session expiry update must occur before generateNewSessionId. Otherwise we were running
				// into issues of calls attempting to read the session ID before a valid expiry had been set
				this.debouncedUpdateSessionExpiry(sessionExpiry);
				return this.generateNewSessionId();
			}
		} catch (err) {
			// Silently swallow exception
		}

		return null;
	}

	getPreviousInteractionSessionId(): string | null {
		return this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
	}

	setInteractionSessionId(persistedSessionId?: string): boolean {
		if (this.validateSessionId(persistedSessionId)) {
			const currentSessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);

			// If we are already on the required session ID, just return true and make no changes
			// This can happen if the persisted session ID in options is the same as the current session ID
			// We noticed this happening in some cases where Confluence's own URL redirect logic was causing
			// race conditions with our URL stripping, resulting in the same query parameter being processed multiple times
			if (currentSessionId === persistedSessionId) {
				return false;
			}

			// Session expiry update must occur before we fire off the dispatchEvent. Otherwise we were running
			// into issues of the event consumer attempting to read the session ID before a valid expiry had been set
			this.debouncedUpdateSessionExpiry(0);

			this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, persistedSessionId!);
			this.safeSessionStorage.removeItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);

			document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));

			return true;
		}

		return false;
	}

	// Must be called with a valid sendEvent function for other methods to work
	setAnalyticsCallback(sendEventCallback?: Function): void {
		if (this.analyticsReady) {
			// If we already have a valid callback, don't over-write it
			return;
		}

		if (sendEventCallback && typeof sendEventCallback === 'function') {
			this.sendEventCallback = sendEventCallback;
			this.analyticsReady = true;
		} else {
			this.analyticsReady = false;
		}
	}

	private validateSessionId(sessionId?: string) {
		// Check if persisted session ID can parse as valid Unix timestamp
		if (sessionId && INTEGER_REGEX.test(sessionId)) {
			const sessionAge = Date.now() - parseInt(sessionId, 10);

			// Validate that timestamp is not a future time, and is also within the TTL
			if (sessionAge >= 0 && sessionAge <= DEFAULT_PERSISTED_SESSION_TTL_MS) {
				return true;
			}
		}

		return false;
	}

	/*
	 * This function retrieves the current session ID and expiry from SessionStorage
	 * It runs the necessary checks to ensure the session ID is valid and within expiry
	 *
	 * It returns the values along with a `validSession` boolean
	 */
	private getCurrentSessionParams(): {
		isValidSession: boolean;
		sessionExpiry: number;
		sessionId: string | null;
	} {
		const sessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
		const sessionExpiry =
			parseInt(
				this.safeSessionStorage.getItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY) || '0',
				10,
			) || 0;

		if (sessionId && !Number.isNaN(sessionExpiry) && sessionExpiry > Date.now()) {
			return { isValidSession: true, sessionExpiry: sessionExpiry, sessionId };
		}

		return { isValidSession: false, sessionExpiry: sessionExpiry, sessionId: sessionId };
	}

	/*
	 * This function sends a GASv3 analytics event indicating the interaction session has started or renewed
	 * It requires this.sendEventCallback to be set via Analytics Web Client to a valid operational event sender function
	 *
	 * Note that Analytics Web Client's callback function will make its own call to getCurrentInteractionSessionId to retrieve the session ID
	 * to attach to the event, so a valid session ID/expiry must be retrievable from SessionStorage before this function is called to avoid infinite loops.
	 *
	 * This is not an ideal solution but it works with the current architecture of Analytics Web Client.
	 */
	private sendInteractionSessionEvent(action: string) {
		this.sendEventCallback &&
			this.sendEventCallback({
				action: action,
				actionSubject: 'interactionSession',
				source: 'interactionComponent',
				tags: ['navigationExperiences'], // InteractionSession events will be registered under this platform product
			});
	}

	private generateNewSessionId = () => {
		if (!this.analyticsReady) {
			return null;
		}

		const currentSessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
		const newSessionId = Date.now().toString();

		this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, newSessionId);

		const { isValidSession } = this.getCurrentSessionParams();

		if (!isValidSession) {
			// If session is not valid at this point, something has gone wrong with Session Storage access
			// Calling sendInteractionSessionEvent now would result in an infinite loop, so we terminate
			return null;
		}

		if (currentSessionId) {
			// Store a copy of the current session ID so we can link sessions if needed
			this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY, currentSessionId);
			this.sendInteractionSessionEvent('renewed');
		} else {
			this.sendInteractionSessionEvent('started');
		}

		// Event listener is in useCrossProductUrl.tsx to update URL query params
		document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));

		return newSessionId;
	};

	private updateSessionExpiry = () => {
		const expiry = Date.now() + this.sessionExpiryTime;
		this.safeSessionStorage.setItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY, expiry.toString());
		return expiry;
	};

	private debouncedUpdateSessionExpiry = (currentExpiry: number) => {
		const { updateSessionExpiry } = this;
		// If the current expiry is out of date by more than 10% of the expiry, update immediately
		if (
			currentExpiry <
			Date.now() + this.sessionExpiryDebounceThresholdPercentage * this.sessionExpiryTime
		) {
			updateSessionExpiry();
		} else {
			if (this.sessionExpiryDebounceTimer) {
				clearTimeout(this.sessionExpiryDebounceTimer);
			}
			// @ts-ignore Something wrong with our types... this is legit
			this.sessionExpiryDebounceTimer = setTimeout(() => {
				updateSessionExpiry();
			}, this.sessionExpiryDebounce);
		}
	};
}
